import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { BackboneInstanceWrapper } from '@bingads-webui-react/backbone-instance-wrapper';
import { validateLocations } from '@bingads-webui-campaign/hotel-common-targets';
import { checkShouldShowErrors, locationTargetsOptionKeys, LocalizationKeys } from '@bingads-webui-campaign-react/wizard-shared-components';

export function useToggleExpander(init = false) {
  const [expanded, setExpanded] = useState(init);

  return [expanded, () => setExpanded(!expanded)];
}

export function useBackboneView(viewFactory, onViewCreated, { managed = false, onViewRemoved, getCreatedView } = {}) {
  const [backboneView, setBackboneView] = useState(null);

  useEffect(() => {
    let view = getCreatedView != null ? getCreatedView() : undefined;
    if (view == null) {
      view = viewFactory();
      onViewCreated(view);
    }
    setBackboneView(view);
    return () => {
      if (onViewRemoved != null) {
        onViewRemoved();
      }
    };
  }, [onViewCreated, viewFactory, onViewRemoved, getCreatedView]);

  if (backboneView) {
    return (<BackboneInstanceWrapper instance={backboneView} managed={managed} />);
  }

  return null;
}

export const usePatch = (patchFunc, key) => useCallback((value) => {
  patchFunc(key, value);
}, [patchFunc, key]);

export const globalErrorMsg = _TL_("*Something's wrong.");

export const getLocationTargetErrorMessage = (location, bidStrategyType) => {
  if (checkShouldShowErrors(location)) {
    return globalErrorMsg;
  }
  if (validateLocations(location, bidStrategyType)) {
    return globalErrorMsg;
  }
  return null;
};

const getLocationsString = (i18n, settings, isAdGroupLevel = false, bidStrategyType) => {
  if (settings == null) {
    return i18n.getString(_TL_('Targeting everywhere.'));
  }

  const {
    targetOption,
    currentLocations = [],
  } = settings;

  let isTargetEverywhere = targetOption === locationTargetsOptionKeys.everywhere;
  if (isAdGroupLevel) {
    isTargetEverywhere = currentLocations.length === 0;
  }

  if (isTargetEverywhere) {
    return i18n.getString(_TL_('Targeting everywhere.'));
  }

  let details = '';
  let includedLocations = currentLocations.filter(x => x.SelectionStatus === 'Included');
  const excludedLocations = currentLocations.filter(x => x.SelectionStatus === 'Excluded');
  if (bidStrategyType === 'Commission') {
    includedLocations = [];
  }
  if (includedLocations.length) {
    details += 'Included locations: ';
    includedLocations.forEach((target, index) => {
      details += target.location;
      details += `: ${i18n.getString(LocalizationKeys[target.bidAdjustmentDropdown])}`;
      details += `: ${target.bidAdjustmentInput}%`;
      if (index < includedLocations.length - 1) {
        details += ', ';
      }
    });
  }
  if (excludedLocations.length) {
    if (includedLocations.length) {
      details += ', ';
    }
    details += 'Excluded locations: ';
    excludedLocations.forEach((target, index) => {
      details += target.location;
      if (index < excludedLocations.length - 1) {
        details += ', ';
      }
    });
  }
  return details;
};

export const getLocationTargetTitleDetails = (i18n, location, isAdGroupLevel, bidStrategyType) => {
  const {
    parentLevelSettings,
    currentLevelSettings,
    useParentLevel,
  } = location;

  if (useParentLevel) {
    if (isAdGroupLevel) {
      return getLocationsString(i18n, parentLevelSettings);
    }
    return i18n.getString(_TL_('Targeting everywhere.'));
  }

  return getLocationsString(i18n, currentLevelSettings, isAdGroupLevel, bidStrategyType);
};

export const getGridTargetTitleDetails = (i18n, params, symbol = ': ', formatDateValue) => {
  let details = '';
  if (params.useParentLevel) {
    const len = params.parentLevelSettings.length;
    params.parentLevelSettings.forEach((key, index) => {
      Object.keys(key).forEach((k, i) => {
        if (key[k] === '' || k === 'Status' || k === 'Id') {
          details += '';
        } else if (k === 'bidAdjustmentInput') {
          details += `: ${key[k]}%`;
        } else if (k === 'bidAdjustmentDropdown') {
          details += `: ${LocalizationKeys[key[k]] ? i18n.getString(LocalizationKeys[key[k]]) : key[k]}`;
        } else if (key[k] instanceof Date) {
          details += `${i === 0 ? '' : symbol}${formatDateValue(key[k])}`;
        } else if (k === 'day' || k === 'dates' || k === 'targeted' || k === 'device') {
          details += `${i === 0 ? '' : symbol}${LocalizationKeys[key[k]] ? i18n.getString(LocalizationKeys[key[k]]) : key[k]}`;
        } else {
          details += `${i === 0 ? '' : symbol}${key[k]}`;
        }
      });
      if (details && index < len - 1) {
        details += ', ';
      }
    });
  } else {
    const len = params.currentLevelSettings.length;
    details = '';
    params.currentLevelSettings.forEach((key, index) => {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < Object.keys(key).length; i++) {
        const k = Object.keys(key)[i];
        if (key[k] === '' || k === 'Status' || k === 'Id') {
          details += '';
        } else if (k === 'bidAdjustmentInput') {
          details += `: ${key[k]}%`;
        } else if (k === 'bidAdjustmentDropdown') {
          details += `: ${LocalizationKeys[key[k]] ? i18n.getString(LocalizationKeys[key[k]]) : key[k]}`;
        } else if (key[k] instanceof Date) {
          details += `${i === 0 ? '' : symbol}${formatDateValue(key[k])}`;
        } else if (k === 'day' || k === 'dates' || k === 'targeted' || k === 'device') {
          details += `${i === 0 ? '' : symbol}${LocalizationKeys[key[k]] ? i18n.getString(LocalizationKeys[key[k]]) : key[k]}`;
        } else {
          details += `${i === 0 ? '' : symbol}${key[k]}`;
        }
      }
      if (details && index < len - 1) {
        details += ', ';
      }
    });
    details = details || `${i18n.getString(_TL_('(no selection)'))}`;
  }
  return details;
};

export const getUrlOptionsDetails = (i18n, urlOptions) => {
  let details = '';
  if (urlOptions.TrackingUrlTemplate) {
    details += `${LocalizationKeys.TrackingUrlTemplate ? i18n.getString(LocalizationKeys.TrackingUrlTemplate) : 'TrackingUrlTemplate'}: ${urlOptions.TrackingUrlTemplate}, `;
  }
  if (urlOptions.FinalUrlSuffix) {
    details += `${LocalizationKeys.FinalUrlSuffix ? i18n.getString(LocalizationKeys.FinalUrlSuffix) : 'FinalUrlSuffix'}: ${urlOptions.FinalUrlSuffix}, `;
  }
  if (urlOptions.UrlCustomParameters && urlOptions.UrlCustomParameters.length > 0) {
    details += `${LocalizationKeys.UrlCustomParameters ? i18n.getString(LocalizationKeys.UrlCustomParameters) : 'UrlCustomParameters'}: ${JSON.stringify(urlOptions.UrlCustomParameters)}, `;
  }
  const reg = /,$/gi;
  details = details.trim().replace(reg, '');
  return details;
};

export const getExclusionsDetails = (i18n, exclusions) => {
  let details = '';
  let exclusionsValue = exclusions;
  if (exclusionsValue.Exclusions) {
    exclusionsValue = exclusions.Exclusions;
  }

  if (exclusionsValue.ExcludedSites) {
    details += `${i18n.getString(LocalizationKeys.ExcludedSites)}: ${exclusionsValue.ExcludedSites}, `;
  }
  if (exclusionsValue.ExcludedIPs) {
    details += `${i18n.getString(LocalizationKeys.ExcludedIPs)}: ${exclusionsValue.ExcludedIPs}, `;
  }
  const reg = /,$/gi;
  details = details.trim().replace(reg, '');
  return details;
};

export const Disclaimer = ({ message }) => (
  <div className={classNames('alert', 'alert-info')} role="alert">
    <div className="alert-image" />
    <div className="alert-info-inline">
      <span>{message}</span>
    </div>
  </div>);

Disclaimer.propTypes = {
  message: PropTypes.string.isRequired,
};
